import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import AccountSettingsModal from '../components/AccountSettingsModal';
import BackBar from '../components/BackBar';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getRevisionDaysRemaining = (paymentDate) => {
  if (!paymentDate) return null;
  const paid = new Date(paymentDate);
  const now = new Date();
  const diffTime = (paid.getTime() + 30 * 24 * 60 * 60 * 1000) - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
};

const DEFAULT_ADDON_INFO = {
  compliance_check: { 
    name: "Council-Specific Compliance Review", 
    description: "Expert review to ensure your plan meets specific council requirements"
  },
  rfi_response: { 
    name: "Council Request for Information (RFI) Response Support", 
    description: "Professional assistance responding to council information requests"
  },
  extra_30_day: { 
    name: "Additional 30 Days PlanGen Access", 
    description: "Extend your access to make updates and regenerate plans"
  },
  bin_storage_review: { 
    name: "Bin Storage Area Design Review", 
    description: "Detailed review and recommendations for bin storage layout"
  }
};

const MyPlans = () => {
  const { user, logout, token, setUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  
  useEffect(() => {
    const checkAddonPurchase = async () => {
      const params = new URLSearchParams(window.location.search);
      const addonsSuccess = params.get('addons_success');
      const pendingAddon = localStorage.getItem('pending_addon_session');
      
      if (pendingAddon) {
        try {
          const { projectId: addonProjectId, sessionId } = JSON.parse(pendingAddon);
          const response = await axios.get(
            `${API}/projects/${addonProjectId}/addon-checkout-status/${sessionId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (response.data.payment_status === 'paid') {
            localStorage.removeItem('pending_addon_session');
            window.location.reload();
          } else {
            localStorage.removeItem('pending_addon_session');
          }
        } catch (err) {
          localStorage.removeItem('pending_addon_session');
        }
      }
      if (addonsSuccess || params.get('addons_cancelled')) {
        window.history.replaceState({}, '', window.location.pathname);
      }
    };
    if (token) checkAddonPurchase();
  }, [token]);
  
  const [showAddonsModal, setShowAddonsModal] = useState(false);
  const [selectedProjectForAddons, setSelectedProjectForAddons] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [processingAddons, setProcessingAddons] = useState(false);
  const [addonPricing, setAddonPricing] = useState({});

  useEffect(() => {
    loadProjects();
    loadPricing();
  }, []);

  const loadPricing = async () => {
    try {
      const response = await axios.get(`${API}/pricing`);
      if (response.data?.addons) setAddonPricing(response.data.addons);
    } catch (error) {
      console.error('Failed to load pricing:', error);
    }
  };

  const getAddonDetails = (addonKey) => {
    const baseInfo = DEFAULT_ADDON_INFO[addonKey] || { name: addonKey, description: '' };
    const dynamicPrice = addonPricing[addonKey]?.price;
    return { ...baseInfo, price: dynamicPrice !== undefined ? dynamicPrice : 0 };
  };

  const loadProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
      setSelectedProjects([]);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = () => navigate('/project/new');
  const handleLogout = () => { logout(); navigate('/login'); };

  const handleSelectProject = (e, projectId) => {
    e.stopPropagation();
    setSelectedProjects(prev =>
      prev.includes(projectId) ? prev.filter(id => id !== projectId) : [...prev, projectId]
    );
  };

  const handleSelectAll = () => {
    setSelectedProjects(prev =>
      prev.length === projects.length ? [] : projects.map(p => p.id)
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedProjects.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedProjects.length} project(s)? This action cannot be undone.`)) return;
    try {
      setDeleting(true);
      await Promise.all(
        selectedProjects.map(projectId =>
          axios.delete(`${API}/projects/${projectId}`, { headers: { Authorization: `Bearer ${token}` } })
        )
      );
      await loadProjects();
    } catch (error) {
      alert('Failed to delete some projects. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenAddonsModal = (e, project) => {
    e.stopPropagation();
    setSelectedProjectForAddons(project);
    setSelectedAddons([]);
    setShowAddonsModal(true);
  };

  const handleToggleAddon = (addonKey) => {
    setSelectedAddons(prev =>
      prev.includes(addonKey) ? prev.filter(k => k !== addonKey) : [...prev, addonKey]
    );
  };

  const calculateAddonsTotal = () => {
    const subtotal = selectedAddons.reduce((total, key) => total + (getAddonDetails(key)?.price || 0), 0);
    return subtotal * 1.1;
  };

  const getAvailableAddonsForProject = () => Object.keys(DEFAULT_ADDON_INFO);

  const handlePurchaseAddons = async () => {
    if (selectedAddons.length === 0 || !selectedProjectForAddons) return;
    try {
      setProcessingAddons(true);
      const response = await axios.post(
        `${API}/projects/${selectedProjectForAddons.id}/purchase-addons`,
        { addons: selectedAddons, origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.checkout_url) {
        localStorage.setItem('pending_addon_session', JSON.stringify({
          projectId: selectedProjectForAddons.id,
          sessionId: response.data.session_id
        }));
        window.location.href = response.data.checkout_url;
      } else {
        await loadProjects();
        setShowAddonsModal(false);
        alert('Add-on services purchased successfully!');
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to process add-on purchase. Please try again.');
    } finally {
      setProcessingAddons(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <BackBar to="/dashboard" label="Dashboard" />
      <SEO title="My Plans" description="Manage your Waste Management Plans" noIndex={true} />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img src="/plangen-consulting-logo.png" alt="PlanGen Consulting" className="h-10 w-auto" />
            </div>
            <div className="flex items-center space-x-4">
              <button onClick={() => navigate('/dashboard')} className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4" />
                </svg>
                Dashboard
              </button>
              <button onClick={() => navigate('/guidelines')} className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Guidelines
              </button>
              <button onClick={() => setShowAccountSettings(true)} className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1" data-testid="account-settings-btn">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Account
              </button>
              <span className="text-sm text-gray-700">Welcome, {user?.full_name}</span>
              <button onClick={handleLogout} className="text-sm text-gray-600 hover:text-gray-900">Logout</button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-4">
            <h2 className="text-3xl font-bold text-gray-900" data-testid="my-plans-title">My Plans</h2>
            <button
              onClick={handleCreateProject}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              data-testid="new-project-btn"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Project
            </button>
          </div>
          <p className="mt-1 text-sm text-gray-600">Create and manage your Waste Management Plans</p>
        </div>

        {/* Selection Actions Bar */}
        {projects.length > 0 && (
          <div className="mb-4 flex items-center justify-between bg-white px-4 py-3 rounded-lg border border-gray-200">
            <div className="flex items-center space-x-4">
              <label className="flex items-center cursor-pointer">
                <input type="checkbox" checked={selectedProjects.length === projects.length && projects.length > 0} onChange={handleSelectAll} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="ml-2 text-sm text-gray-700">{selectedProjects.length === projects.length ? 'Deselect All' : 'Select All'}</span>
              </label>
              {selectedProjects.length > 0 && (
                <span className="text-sm text-gray-500">{selectedProjects.length} project(s) selected</span>
              )}
            </div>
            {selectedProjects.length > 0 && (
              <button onClick={handleDeleteSelected} disabled={deleting} className="inline-flex items-center px-3 py-1.5 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50">
                {deleting ? 'Deleting...' : (
                  <>
                    <svg className="-ml-1 mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete Selected
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Projects List */}
        {loading ? (
          <div className="text-center py-12"><p className="text-gray-600">Loading projects...</p></div>
        ) : projects.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No projects yet</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating your first Waste Management Plan.</p>
            <div className="mt-6">
              <button onClick={handleCreateProject} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">
                Create Your First Project
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {(() => {
              const bundledGroups = {};
              const standaloneProjects = [];
              projects.forEach(project => {
                if (project.bundle_id) {
                  if (!bundledGroups[project.bundle_id]) bundledGroups[project.bundle_id] = [];
                  bundledGroups[project.bundle_id].push(project);
                } else {
                  standaloneProjects.push(project);
                }
              });
              Object.values(bundledGroups).forEach(group => {
                group.sort((a, b) => (b.is_bundle_primary ? 1 : 0) - (a.is_bundle_primary ? 1 : 0));
              });
              return (
                <>
                  {Object.entries(bundledGroups).map(([bundleId, bundleProjects]) => {
                    const primaryProject = bundleProjects.find(p => p.is_bundle_primary);
                    const baseName = primaryProject?.project_name?.replace(' - OpWMP', '') || 'Bundled Pack';
                    return (
                      <div key={bundleId} className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 border-2 border-purple-200">
                        <div className="flex items-center gap-2 mb-4 min-w-0">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-purple-600 text-white flex-shrink-0">
                            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                            Bundled Pack
                          </span>
                          <span className="text-sm text-purple-700 font-medium truncate">{baseName}</span>
                        </div>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          {bundleProjects.map((project) => {
                            const isLocked = !project.is_bundle_primary && project.current_step === 1 && !project.paid;
                            const planLabel = project.plan_type === 'operational_wmp' ? 'OpWMP' : project.plan_type === 'construction_management_plan' ? 'CMP' : 'D&CWMP';
                            const totalSteps = (project.plan_type === 'demolition_construction_wmp' || project.plan_type === 'construction_management_plan') ? 7 : 8;
                            return (
                              <div key={project.id} className={`bg-white overflow-hidden shadow-sm rounded-lg border-2 transition-all ${isLocked ? 'border-gray-300 opacity-75 cursor-not-allowed' : selectedProjects.includes(project.id) ? 'border-blue-500 bg-blue-50 cursor-pointer hover:shadow-md' : 'border-gray-200 cursor-pointer hover:shadow-md'}`} onClick={() => !isLocked && navigate(`/project/${project.id}`)}>
                                <div className="p-5">
                                  <div className="flex items-start justify-between">
                                    <div className="flex items-start space-x-3">
                                      {!isLocked && (
                                        <input type="checkbox" checked={selectedProjects.includes(project.id)} onChange={(e) => handleSelectProject(e, project.id)} onClick={(e) => e.stopPropagation()} className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                                      )}
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${project.plan_type === 'operational_wmp' ? 'bg-blue-100 text-blue-800' : project.plan_type === 'construction_management_plan' ? 'bg-indigo-100 text-indigo-800' : 'bg-amber-100 text-amber-800'}`}>{planLabel}</span>
                                          {project.is_bundle_primary && <span className="text-xs text-purple-600 font-medium">Primary</span>}
                                        </div>
                                        {isLocked && (
                                          <div className="mt-2 flex items-center text-xs text-gray-500">
                                            <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                            Complete OpWMP Step 6 to unlock
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${project.paid ? 'bg-green-100 text-green-800' : isLocked ? 'bg-gray-100 text-gray-600' : 'bg-yellow-100 text-yellow-800'}`}>
                                      {project.paid ? 'Paid' : isLocked ? 'Locked' : 'Draft'}
                                    </span>
                                  </div>
                                  {!isLocked && (
                                    <>
                                      <div className="mt-3 ml-7">
                                        <div className="flex items-center text-sm text-gray-500"><span>Step {project.current_step} of {totalSteps}</span></div>
                                        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                                          <div className={`h-2 rounded-full ${project.plan_type === 'demolition_construction_wmp' ? 'bg-amber-600' : project.plan_type === 'construction_management_plan' ? 'bg-indigo-600' : 'bg-blue-600'}`} style={{ width: `${(project.current_step / totalSteps) * 100}%` }} />
                                        </div>
                                      </div>
                                      {project.selected_addons && project.selected_addons.length > 0 && (
                                        <div className="mt-3 ml-7">
                                          <div className="flex flex-wrap gap-1.5">
                                            {project.selected_addons.map(addon => (
                                              <span key={addon} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                                                {addon === 'compliance_check' ? 'Compliance Review' : addon === 'rfi_response' ? 'RFI Response' : addon === 'extra_30_day' ? '+30 Days Access' : addon === 'bin_storage_review' ? 'Bin Storage Review' : addon}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  {standaloneProjects.length > 0 && (
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                      {standaloneProjects.map((project) => {
                        const isLocked = project.locked || (project.lock_date && new Date(project.lock_date) <= new Date());
                        const daysRemaining = project.lock_date ? Math.max(0, Math.ceil((new Date(project.lock_date) - new Date()) / (1000 * 60 * 60 * 24))) : null;
                        return (
                          <div key={project.id} className={`bg-white overflow-hidden shadow-sm rounded-lg border-2 transition-shadow ${isLocked ? 'border-red-300 opacity-75 cursor-not-allowed' : selectedProjects.includes(project.id) ? 'border-blue-500 bg-blue-50 hover:shadow-md cursor-pointer' : 'border-gray-200 hover:shadow-md cursor-pointer'}`} onClick={() => !isLocked && navigate(`/project/${project.id}`)}>
                            <div className="p-6">
                              <div className="flex items-start justify-between">
                                <div className="flex items-start space-x-3 min-w-0 flex-1">
                                  <input type="checkbox" checked={selectedProjects.includes(project.id)} onChange={(e) => handleSelectProject(e, project.id)} onClick={(e) => e.stopPropagation()} className="mt-1 h-4 w-4 flex-shrink-0 text-blue-600 border-gray-300 rounded focus:ring-blue-500" disabled={isLocked} />
                                  <div className="min-w-0 flex-1">
                                    <h3 className="text-lg font-medium text-gray-900 break-words">{project.project_name}</h3>
                                    <span className={`inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-semibold ${project.plan_type === 'operational_wmp' ? 'bg-blue-100 text-blue-800' : project.plan_type === 'construction_management_plan' ? 'bg-indigo-100 text-indigo-800' : 'bg-amber-100 text-amber-800'}`}>
                                      {project.plan_type === 'operational_wmp' ? 'OpWMP' : project.plan_type === 'construction_management_plan' ? 'CMP' : 'D&CWMP'}
                                    </span>
                                  </div>
                                </div>
                                {isLocked ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Locked</span>
                                ) : (
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${project.paid ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                    {project.paid ? 'Paid' : 'Draft'}
                                  </span>
                                )}
                              </div>
                              <p className="mt-2 ml-7 text-sm text-gray-600">{project.state_code} - {project.development_type}</p>
                              <div className="mt-4 ml-7">
                                <div className="flex items-center text-sm text-gray-500">
                                  <span>Step {project.current_step} of {(project.plan_type === 'demolition_construction_wmp' || project.plan_type === 'construction_management_plan') ? 7 : 8}</span>
                                </div>
                                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                                  <div className={`h-2 rounded-full ${isLocked ? 'bg-red-400' : project.plan_type === 'demolition_construction_wmp' ? 'bg-amber-600' : project.plan_type === 'construction_management_plan' ? 'bg-indigo-600' : 'bg-blue-600'}`} style={{ width: `${(project.current_step / ((project.plan_type === 'demolition_construction_wmp' || project.plan_type === 'construction_management_plan') ? 7 : 8)) * 100}%` }} />
                                </div>
                              </div>
                              <div className="mt-4 ml-7 text-xs text-gray-500">Created {new Date(project.created_at).toLocaleDateString()}</div>
                              {project.paid && !isLocked && (
                                <div className="mt-2 ml-7">
                                  {(() => {
                                    const revisionDays = getRevisionDaysRemaining(project.payment_date);
                                    if (revisionDays === null) return null;
                                    return (
                                      <div className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${revisionDays > 15 ? 'bg-green-50 text-green-700 border border-green-200' : revisionDays > 5 ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                                        <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                        {revisionDays > 0 ? `${revisionDays} day${revisionDays !== 1 ? 's' : ''} left for free revisions` : 'Revision period ended'}
                                      </div>
                                    );
                                  })()}
                                </div>
                              )}
                              {isLocked && (
                                <div className="mt-3 ml-7 p-2 bg-red-50 rounded-md">
                                  <p className="text-xs text-red-700">30-day access period has expired. Purchase "Additional 30 Days" to unlock.</p>
                                </div>
                              )}
                              {project.selected_addons && project.selected_addons.length > 0 && (
                                <div className="mt-3 ml-7">
                                  <div className="flex flex-wrap gap-1.5">
                                    {project.selected_addons.map(addon => (
                                      <span key={addon} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                                        {addon === 'compliance_check' ? 'Compliance Review' : addon === 'rfi_response' ? 'RFI Response' : addon === 'extra_30_day' ? '+30 Days Access' : addon === 'bin_storage_review' ? 'Bin Storage Review' : addon}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {project.paid && (
                                <div className="mt-3 ml-7">
                                  <button onClick={(e) => handleOpenAddonsModal(e, project)} className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${isLocked ? 'text-red-700 bg-red-50 border border-red-200 hover:bg-red-100' : 'text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100'}`}>
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                                    {isLocked ? 'Unlock Project' : 'Purchase Add-ons'}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </main>

      {/* Account Settings Modal */}
      <AccountSettingsModal isOpen={showAccountSettings} onClose={() => setShowAccountSettings(false)} user={user} onUserUpdate={(updatedUser) => { setUser(updatedUser); setShowAccountSettings(false); }} />

      {/* Add-ons Purchase Modal */}
      {showAddonsModal && selectedProjectForAddons && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" onClick={() => setShowAddonsModal(false)}></div>
            <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full mx-auto p-6 z-10">
              <button onClick={() => setShowAddonsModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Purchase Add-on Services</h2>
              <p className="text-sm text-gray-600 mb-4">For: <span className="font-medium">{selectedProjectForAddons.project_name}</span></p>
              {getAvailableAddonsForProject().length === 0 ? (
                <div className="py-8 text-center">
                  <p className="mt-4 text-gray-600">You've already purchased all available add-on services for this project.</p>
                </div>
              ) : (
                <>
                  <div className="space-y-3 mb-6">
                    {getAvailableAddonsForProject().map(addonKey => {
                      const addon = getAddonDetails(addonKey);
                      const isSelected = selectedAddons.includes(addonKey);
                      return (
                        <div key={addonKey} onClick={() => handleToggleAddon(addonKey)} className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3">
                              <input type="checkbox" checked={isSelected} onChange={() => {}} className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                              <div>
                                <h4 className="text-sm font-medium text-gray-900">{addon.name}</h4>
                                <p className="text-xs text-gray-500 mt-1">{addon.description}</p>
                              </div>
                            </div>
                            <span className="text-sm font-semibold text-gray-900">${addon.price}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {selectedProjectForAddons.selected_addons && selectedProjectForAddons.selected_addons.length > 0 && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-2">Already purchased:</p>
                      <div className="flex flex-wrap gap-1">
                        {selectedProjectForAddons.selected_addons.map(addon => (
                          <span key={addon} className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">{getAddonDetails(addon)?.name || addon}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-sm text-gray-600">Total (inc. GST):</span>
                      <span className="text-xl font-bold text-gray-900">${calculateAddonsTotal().toFixed(2)}</span>
                    </div>
                    <button onClick={handlePurchaseAddons} disabled={selectedAddons.length === 0 || processingAddons} className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                      {processingAddons ? 'Processing...' : `Purchase ${selectedAddons.length} Add-on${selectedAddons.length !== 1 ? 's' : ''}`}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPlans;
